import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, ArrowUpRight } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const HERO_IMG =
  "https://images.unsplash.com/photo-1529156069898-49953e39b3ac?auto=format&fit=crop&w=1800&q=80";

function MaskedLine({ children, delay, className }) {
  return (
    <span className="block overflow-hidden">
      <motion.span
        className={`block ${className}`}
        initial={{ y: "115%" }}
        animate={{ y: 0 }}
        transition={{ duration: 1.2, delay, ease: EASE }}
      >
        {children}
      </motion.span>
    </span>
  );
}

export default function Hero({ t, onPrimary, onSecondary }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);
  const fade = useTransform(scrollYProgress, [0, 0.8], [1, 0]);

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero-section"
      className="relative flex min-h-screen flex-col justify-center overflow-hidden"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt=""
          className="h-[120%] w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-void/70 via-void/60 to-void" />
      </motion.div>

      <motion.div style={{ opacity: fade }} className="relative z-10 mx-auto w-full max-w-7xl px-6 pt-24">
        <MaskedLine delay={0.35} className="mb-8 font-display text-xs font-bold uppercase tracking-[0.25em] text-flame md:text-sm">
          <span data-testid="hero-overline">{t.overline}</span>
        </MaskedLine>

        <h1 className="font-display font-black uppercase leading-[0.82] tracking-tighter">
          <MaskedLine delay={0.5} className="text-[clamp(4.5rem,16vw,17rem)] text-flame">
            <span data-testid="hero-title">{t.title}</span>
          </MaskedLine>
        </h1>

        <div className="mt-10 flex flex-col gap-10 md:flex-row md:items-end md:justify-between">
          <h2 className="font-display text-2xl font-medium leading-tight text-bone sm:text-3xl lg:text-4xl">
            <MaskedLine delay={0.85}>
              <span data-testid="hero-tagline-1">{t.tagline1}</span>
            </MaskedLine>
            <MaskedLine delay={1.0}>
              <span data-testid="hero-tagline-2" className="font-editorial italic text-flame">
                {t.tagline2}
              </span>
            </MaskedLine>
          </h2>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 1.25, ease: EASE }}
            className="max-w-md"
          >
            <p data-testid="hero-sub" className="mb-8 text-base leading-relaxed text-ash md:text-lg">
              {t.sub}
            </p>
            <div className="flex flex-wrap items-center gap-5">
              <motion.button
                data-testid="hero-primary-cta"
                onClick={onPrimary}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.96 }}
                className="rounded-full bg-flame px-8 py-4 font-display text-sm font-bold uppercase tracking-wide text-white transition-colors duration-300 hover:bg-flame-hover"
              >
                {t.primaryCta}
              </motion.button>
              <button
                data-testid="hero-secondary-cta"
                onClick={onSecondary}
                className="group inline-flex items-center gap-1.5 font-display text-sm font-bold uppercase tracking-wide text-bone"
              >
                <span className="relative">
                  {t.secondaryCta}
                  <span className="absolute -bottom-1 left-0 h-px w-0 bg-flame transition-all duration-500 group-hover:w-full" />
                </span>
                <ArrowUpRight size={16} className="text-flame transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
              </button>
            </div>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.8, duration: 1 }}
        className="absolute bottom-8 left-6 z-10 flex items-center gap-3 text-ash"
      >
        <motion.span animate={{ y: [0, 6, 0] }} transition={{ repeat: Infinity, duration: 2, ease: "easeInOut" }}>
          <ArrowDown size={16} />
        </motion.span>
        <span data-testid="hero-scroll-hint" className="font-display text-xs uppercase tracking-[0.2em]">
          {t.scroll}
        </span>
      </motion.div>
    </section>
  );
}
